import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import groupMessages from 'features/Chat/utils/groupMessages'
import MessageBubble, { TRunPosition } from './MessageBubble'
import DeliveryStatus from './DeliveryStatus'
import TypingIndicator from './TypingIndicator'
import type { IChatHeader, IChatMessage, IReadStateRow } from 'features/Chat/Chat.types'

export interface Props {
	messages: IChatMessage[]
	userId: string
	header?: IChatHeader
	readState?: IReadStateRow[]
	/** True when the thread has more than one other participant. */
	isGroup: boolean
	typists: string[]
	onRetry?: (clientId: string) => void
	/** Saves new text for a message. */
	onEdit?: (messageId: string, text: string) => Promise<void>
	/** Sets, changes or clears the current user's reaction. */
	onReact?: (messageId: string, emoji: string) => void
	/** Fetches the page before the oldest message held. */
	onLoadEarlier?: () => Promise<void> | void
	/** Reports how close the scroller is to the bottom, for read receipts. */
	onScrollStateChange?: (atBottom: boolean) => void
}

const positionInRun = (index: number, length: number): TRunPosition => {
	if (length === 1) return 'single'
	if (index === 0) return 'first'
	if (index === length - 1) return 'last'
	return 'middle'
}

const NEAR_BOTTOM_PX = 40

const MessageGroupList = ({
	messages,
	userId,
	header,
	readState,
	isGroup,
	typists,
	onRetry,
	onEdit,
	onReact,
	onLoadEarlier,
	onScrollStateChange,
}: Props) => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const [isLoadingEarlier, setIsLoadingEarlier] = useState(false)
	// Scroll height captured before older messages are prepended, so the view
	// can be pinned to the message the user was reading instead of jumping.
	const pendingRestore = useRef<number | null>(null)
	const wasAtBottom = useRef(true)

	const days = groupMessages(messages, userId)

	// The newest outgoing message is the only one that carries a receipt line.
	const lastOutgoing = [...(messages || [])]
		.reverse()
		.find(m => m.senderId?._id === userId)

	useLayoutEffect(() => {
		const el = scrollRef.current
		if (!el) return

		if (pendingRestore.current !== null) {
			el.scrollTop += el.scrollHeight - pendingRestore.current
			pendingRestore.current = null
			return
		}

		// Only auto-scroll when the reader was already at the bottom, so an
		// incoming message does not yank them out of the history they are
		// reading.
		if (wasAtBottom.current) el.scrollTop = el.scrollHeight
	}, [messages])

	useEffect(() => {
		const el = scrollRef.current
		if (el) el.scrollTop = el.scrollHeight
		// Jump to the bottom without animation when the thread changes.
	}, [header?._id])

	const handleScroll = async () => {
		const el = scrollRef.current
		if (!el) return

		const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
		if (atBottom !== wasAtBottom.current) {
			wasAtBottom.current = atBottom
			onScrollStateChange?.(atBottom)
		}

		if (el.scrollTop < 60 && onLoadEarlier && !isLoadingEarlier && messages.length > 0) {
			setIsLoadingEarlier(true)
			pendingRestore.current = el.scrollHeight
			try {
				await onLoadEarlier()
			} finally {
				setIsLoadingEarlier(false)
			}
		}
	}

	return (
		<div
			ref={scrollRef}
			onScroll={handleScroll}
			data-testid="chat-popup-messages"
			className="relative flex-1 overflow-y-auto overscroll-contain px-3 py-2"
		>
			{isLoadingEarlier && (
				<div className="py-2 text-center text-[11px] text-gray-400">Loading earlier messages…</div>
			)}

			{days.map(day => (
				<section key={day.key} className="relative">
					{/* sticky inside the scroll container gives the iOS floating
					    date pill with no scroll listener. */}
					<div className="sticky top-0 z-10 flex flex-col items-center py-2">
						<span className="rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 backdrop-blur-sm">
							{day.label}
						</span>
						<span className="mt-0.5 text-[11px] text-gray-400">{day.firstTime}</span>
					</div>

					{day.runs.map((run, runIndex) => (
						<div key={`${day.key}-${runIndex}`} className="mt-2 space-y-0.5">
							{/* Sender name only above the first bubble of an
							    incoming run, and only in a group: in a 1:1 the
							    avatar already says who it is. */}
							{isGroup && !run.isOutgoing && (
								<span className="ml-9 block text-[11px] text-gray-500">
									{run.messages[0].senderId?.name?.split(' ')[0]}
								</span>
							)}

							{run.messages.map((message, i) => {
								const isLastOfRun = i === run.messages.length - 1
								// Every incoming run gets the sender's avatar against
								// its last bubble, group or not. The rest of the run
								// is indented to line up behind it.
								const showAvatar = !run.isOutgoing && isLastOfRun
								return (
									<div
										key={message._id || message.clientId || i}
										className={cn(
											'flex items-end gap-2',
											!run.isOutgoing && !showAvatar && 'ml-9',
										)}
									>
										{showAvatar && (
											<Avatar className="h-7 w-7 shrink-0" title={message.senderId?.name}>
												<AvatarImage
													src={message.senderId?.image}
													alt={message.senderId?.name}
												/>
												<AvatarFallback className="text-[11px] font-medium text-gray-600">
													{message.senderId?.name?.charAt(0).toUpperCase() || '?'}
												</AvatarFallback>
											</Avatar>
										)}
										<div className="min-w-0 flex-1">
											<MessageBubble
												message={message}
												isOutgoing={run.isOutgoing}
												position={positionInRun(i, run.messages.length)}
												userId={userId}
												// Only the user's own latest message is editable,
												// matching what the server will accept.
												isEditable={!!lastOutgoing && message._id === lastOutgoing._id}
												onRetry={onRetry}
												onEdit={onEdit}
												onReact={onReact}
											/>
										</div>
									</div>
								)
							})}
						</div>
					))}
				</section>
			))}

			{lastOutgoing && (
				<DeliveryStatus
					className="mt-1"
					message={lastOutgoing}
					header={header}
					readState={readState}
					userId={userId}
				/>
			)}

			{/* ml-9 lines the dots up with the incoming bubbles above, which now
			    all sit behind an avatar. */}
			<TypingIndicator className="ml-9 mt-2" typists={typists} showNames={isGroup} />
		</div>
	)
}

export default MessageGroupList
